import { UIButton, bindUIFocus } from '../ui/UIButton';
import { UITheme } from '../ui/UITheme';
import * as Phaser from 'phaser';
import { LocalizationManager } from '../managers/LocalizationManager';
import { PlayerProfile, RunSummary } from '../data/ProfileState';
import { getSharedProfileRuntime, ProfileRuntime } from '../managers/ProfileRuntime';
import { createVersionLabel } from '../ui/VersionLabel';
import { createTelegramLink } from '../ui/TelegramLink';
import { createLogger } from '../utils/Logger';

export class GameOverScene extends Phaser.Scene {
    private score: number = 0;
    private summary: RunSummary | null = null;
    private profile: PlayerProfile | null = null;
    private profileRuntime!: ProfileRuntime;
    private readonly logger = createLogger('GameOverScene');

    private bgRect!: Phaser.GameObjects.Rectangle;
    private titleText!: Phaser.GameObjects.Text;
    private scoreText!: Phaser.GameObjects.Text;
    private statsText?: Phaser.GameObjects.Text;
    private restartBtn!: UIButton;
    private versionLabel!: Phaser.GameObjects.Text | null;
    private telegramLink!: Phaser.GameObjects.Text;

    constructor() {
        super('GameOverScene');
    }

    init(data: { score: number; summary?: RunSummary; profile?: PlayerProfile }) {
        this.score = data.score || 0;
        this.summary = data.summary ?? null;
        this.profile = data.profile ?? null;
    }

    create() {
        this.profileRuntime = getSharedProfileRuntime();
        this.profile = this.profile ?? this.profileRuntime.getProfileSnapshot().profile ?? null;
        // Audio
        // We can just use scene.sound directly since this is simple scene, 
        // but let's be consistent or just use basic.
        this.sound.stopAll();
        // this.sound.play('gameover'); // if exists

        const { width, height } = this.scale;

        // Background
        this.bgRect = this.add.rectangle(0, 0, width, height, UITheme.background, 1).setOrigin(0);

        const loc = LocalizationManager.getInstance();

        // Title
        this.titleText = this.add.text(width / 2, height * 0.3, loc.getText('screen.gameover.title'), {
            fontFamily: UITheme.heading,
            fontSize: '28px',
            color: '#e47c69',
            fontStyle: 'bold'
        }).setOrigin(0.5);

        // Score
        this.scoreText = this.add.text(width / 2, height * 0.45, loc.getText('screen.gameover.score', [this.score]), {
            fontFamily: UITheme.body,
            fontSize: '20px',
            color: UITheme.text
        }).setOrigin(0.5);

        if (this.summary && this.profile) {
            this.statsText = this.add.text(width / 2, height * 0.52, [
                loc.getText('profile.run.time', [Math.floor(this.summary.survivedSeconds)]),
                loc.getText('profile.records.best_time', [Math.floor(this.profile.records.bestSurvivedSeconds)])
            ], {
                fontFamily: UITheme.body,
                fontSize: '18px',
                color: UITheme.muted,
                align: 'center'
            }).setOrigin(0.5);
        }

        // Return to Menu Button
        this.restartBtn = new UIButton(this, width / 2, height * 0.70, loc.getText('ui.main_menu'), () => this.scene.start('MainMenu'), 260, 52);

        this.versionLabel = createVersionLabel(this);
        this.telegramLink = createTelegramLink(this);

        bindUIFocus(this, [this.restartBtn, {setFocused: focused => this.telegramLink.setStyle({color:focused?'#ecc778':UITheme.text}),activate:()=>window.open('https://t.me/+zDjN3qxWP6EyY2Q6','_blank','noopener,noreferrer')}]);
        // Resize handler
        this.scale.on('resize', this.resizeLayout, this);
        this.events.once('shutdown', () => {
            this.scale.off('resize', this.resizeLayout, this);
        });

        // Trigger initial resize layout positioning
        this.resizeLayout();
    }

    private resizeLayout() {
        const width = this.scale.width;
        const height = this.scale.height;
        const scaleFactor = 1;
        this.logger.debug('resizeLayout', { width, height, scaleFactor });

        if (this.bgRect) {
            this.bgRect.setSize(width, height);
        }
        if (this.titleText) {
            this.titleText.setPosition(width / 2, height * 0.18);
            this.titleText.setWordWrapWidth(width-32);
            this.titleText.setScale(scaleFactor);
        }
        if (this.scoreText) {
            this.scoreText.setPosition(width / 2, height * 0.43);
            this.scoreText.setScale(scaleFactor);
        }
        if (this.statsText) {
            this.statsText.setPosition(width / 2, height * 0.55);
            this.statsText.setScale(scaleFactor);
        }
        if (this.restartBtn) {
            this.restartBtn.setPosition(width / 2, height * 0.72);
            this.restartBtn.setScale(scaleFactor);
        }
        if (this.versionLabel) {
            this.versionLabel.setPosition(width - 16, height - 12);
        }
        this.telegramLink.setPosition(width / 2, height * 0.89);
        this.telegramLink.setWordWrapWidth(width-32);

    }
}
